import image from '../images/2.jpg'
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Code, 
  Palette, 
  Zap, 
  ArrowRight, 
  Github, 
  Linkedin, 
  Mail,
  Download,
  Star,
  Sparkles
} from 'lucide-react';
import cv from '../images/cv.docx'


const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const floatingAnimation = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        
        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <motion.header 
          className="text-center mb-16"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
            variants={fadeInUp}
          >
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-white font-medium text-sm">Available for new opportunities</span>
          </motion.div>

          <motion.h1
            className="text-6xl md:text-8xl font-bold mb-6 leading-tight"
            variants={fadeInUp}
          >
            <span className="text-white">Hello, I'm </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Ero Eghosa Daniel
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            Frontend Developer & UI/UX Enthusiast crafting beautiful, efficient web experiences
          </motion.p>

          {/* Animated Skills Tags */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-12"
            variants={fadeInUp}
          >
            {['React', 'TypeScript', 'Next.js', 'Tailwind CSS', 'Framer Motion'].map((skill, index) => (
              <motion.span
                key={skill}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium"
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.2)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                {skill}
              </motion.span>
            ))}
          </motion.div>
        </motion.header>

        {/* Profile Image Section */}
        <motion.div 
          className="relative mb-16"
          variants={floatingAnimation}
          animate="animate"
        >
          <div className="relative">
            {/* Glowing Ring */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full blur-lg opacity-75 animate-pulse"></div>
            
            {/* Profile Image */}
            <motion.div
              className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-white/20 backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <img 
                src={image}
                alt="Ero Eghosa Daniel - Frontend Developer" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </motion.div>

            {/* Floating Icons */}
            <motion.div
              className="absolute -top-4 -right-4 bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <Code className="w-6 h-6 text-white" />
            </motion.div>
            
            <motion.div
              className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-full"
              animate={{ rotate: -360 }}
              transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            >
              <Palette className="w-6 h-6 text-white" />
            </motion.div>
          </div>
        </motion.div>

        {/* Description Section */}
        <motion.div 
          className="text-center mb-16 max-w-4xl"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div
            className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 md:p-12 mb-8"
            variants={fadeInUp}
            whileHover={{ y: -5, scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Passionate About Creating Digital Experiences
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              I specialize in creating efficient and user-friendly web interfaces with a focus on 
              problem-solving and intuitive design. As a frontend developer, I bring those designs 
              to life using modern frameworks and technologies, ensuring every pixel serves a purpose.
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">50+</div>
                <div className="text-slate-300 text-sm">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">3+</div>
                <div className="text-slate-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-400" />
                  ))}
                </div>
                <div className="text-slate-300 text-sm">Client Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Navigation Buttons */}
        <motion.nav 
          className="flex flex-col sm:flex-row gap-4 mb-16"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div variants={fadeInUp}>
            <Link 
              to="/projects" 
              className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-2xl hover:shadow-purple-500/25 flex items-center gap-3 transition-all duration-300"
            >
              <Zap className="w-6 h-6" />
              View My Projects
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <Link 
              to="/skills" 
              className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 flex items-center gap-3 transition-all duration-300"
            >
              <Code className="w-6 h-6" />
              My Skills
            </Link>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <Link 
              to="/contact" 
              className="group bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 flex items-center gap-3 transition-all duration-300"
            >
              <Mail className="w-6 h-6" />
              Contact Me
            </Link>
          </motion.div>
        </motion.nav>

        {/* Social Links & Resume */}
        <motion.div 
          className="flex flex-col sm:flex-row items-center gap-6"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div 
            className="flex gap-4"
            variants={fadeInUp}
          >
            <motion.a
              href="https://github.com/EroDaniel123"
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-3 rounded-full text-white hover:bg-white/20 transition-all duration-300"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Github className="w-6 h-6" />
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/in/ero-daniel-0b52a9271/"
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-3 rounded-full text-white hover:bg-white/20 transition-all duration-300"
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Linkedin className="w-6 h-6" />
            </motion.a>
            <motion.a
  href="mailto:erodaniel310@gmail.com"
  className="bg-white/10 backdrop-blur-sm border border-white/20 p-3 rounded-full text-white hover:bg-white/20 transition-all duration-300"
  whileHover={{ scale: 1.1, y: -2 }}
  whileTap={{ scale: 0.95 }}
>
  <Mail className="w-6 h-6" />
</motion.a>


          </motion.div>

     <a href={cv} download>
  <motion.button
    className="group bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:shadow-lg transition-all duration-300"
    variants={fadeInUp}
    whileHover={{ scale: 1.05, y: -2 }}
    whileTap={{ scale: 0.95 }}
  >
    <Download className="w-5 h-5" />
    Download Resume
  </motion.button>
</a>

        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;